// src/resources/entityReports/EntityReportShow.js
import * as React from 'react';
import {
    DateField,
    FunctionField,
    Show,
    SimpleShowLayout,
    TextField,
} from 'react-admin';
import { Box, Chip, Stack, Typography } from '@mui/material';
import StatusField from '../../components/StatusField';
import EntityReportActionsField from './EntityReportActionsField';
import { formatTypeLabel, getEntitySubtitle, getEntityTitle, getLatestReports, getReasonStats, getReporterList } from './helpers';

const renderType = (record) => <Chip label={formatTypeLabel(record?.type)} size="small" />;

const renderEntity = (record) => (
    <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {getEntityTitle(record)}
        </Typography>
        {getEntitySubtitle(record) && (
            <Typography variant="body2" color="text.secondary">
                {getEntitySubtitle(record)}
            </Typography>
        )}
    </Box>
);

const renderReasons = (record) => {
    const reasons = getReasonStats(record);
    if (!reasons.length) {
        return <Typography variant="body2">Aucun motif enregistré.</Typography>;
    }
    return (
        <Stack direction="row" spacing={1} flexWrap="wrap">
            {reasons.map((reason) => (
                <Chip
                    key={`${reason.reason}-${reason.count}`}
                    label={`${reason.reason || 'Autre'} · ${reason.count ?? 0}`}
                />
            ))}
        </Stack>
    );
};

const renderReporters = (record) => {
    const reporters = getReporterList(record);
    if (!reporters.length) {
        return <Typography variant="body2">Aucun rapporteur listé.</Typography>;
    }
    return (
        <Stack direction="row" spacing={1} flexWrap="wrap">
            {reporters.map((user) => (
                <Chip
                    key={user.userId || user.username}
                    avatar={user.avatarUrl ? <Box component="img" src={user.avatarUrl} alt={user.username} sx={{ width: 24, height: 24 }} /> : undefined}
                    label={user.username || 'Utilisateur'}
                />
            ))}
        </Stack>
    );
};

const renderTimeline = (record) => {
    const entries = getLatestReports(record);
    if (!entries.length) {
        return <Typography variant="body2">Pas encore d'historique détaillé.</Typography>;
    }
    return (
        <Stack spacing={1.5}>
            {entries.map((entry) => (
                <Box key={entry.reportId || entry.createdAt} sx={{ borderLeft: '3px solid #e0e0e0', pl: 2 }}>
                    <Typography variant="subtitle2">
                        {entry.reporter?.username || 'Utilisateur'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {entry.reason || 'Motif non fourni'}
                    </Typography>
                    {entry.context && (
                        <Typography variant="caption" color="text.secondary" display="block">
                            {entry.context}
                        </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary">
                        {entry.createdAt ? new Date(entry.createdAt).toLocaleString() : ''}
                    </Typography>
                </Box>
            ))}
        </Stack>
    );
};

export default function EntityReportShow() {
    return (
        <Show resource="entity-reports" title="Signalement d'entité">
            <SimpleShowLayout>
                <TextField source="id" label="ID" />
                <FunctionField label="Type" render={renderType} />
                <StatusField source="status" label="Statut" />
                <DateField source="lastReportedAt" label="Dernier signalement" showTime />
                <FunctionField label="Élément" render={renderEntity} />
                <FunctionField label="Motifs" render={renderReasons} />
                <FunctionField label="Reporteurs" render={renderReporters} />
                <FunctionField label="Historique" render={renderTimeline} />
                <FunctionField label="Actions" render={(record) => <EntityReportActionsField variant="show" record={record} />} />
            </SimpleShowLayout>
        </Show>
    );
}
